// The hub. Ordered into three bands — what you check on, what you set up and then mostly leave,
// and what you look back at — so the order reads as deliberate rather than arbitrary.
export interface MoreMenuProps {
  nav: (view: View) => void;
  data: DashboardData;
  today: string;
  month: string;
  profile: Owner;
  /** Prices and receipt library need the API; grey them while offline. */
  online?: boolean;
}
import { CalendarClock, ChartNoAxesCombined, ChevronRight, HandCoins, Images, PiggyBank, Plane, ReceiptText, ShoppingBasket, Target, Trophy, Users, Wallet } from "lucide-react";
import type { DashboardData, Owner } from "../types";
import { plural } from "../lib/finance";
import { budgetedPairs, categorySpend, resolveBudget } from "../lib/budgets";
import type { View } from "./views";

function hubStats(data: DashboardData, today: string, month: string, profile: Owner) {
  const due = (data.recurring || []).filter((b) => b.active && b.nextDue.slice(0, 10) < today).length;
  const openIous = (data.ious || []).filter((i) => i.status === "Open").length;
  // Caps for the signed-in person, plus household-wide ones they share.
  const budgets = data.budgets || [];
  const pairs = budgetedPairs(budgets).filter(
    (p) => p.owner === profile || p.owner === "Household",
  );
  let over = 0;
  let tracked = 0;
  for (const { owner, category } of pairs) {
    const budget = resolveBudget(budgets, owner, category, month);
    if (!budget || budget.amount === null) continue;
    tracked += 1;
    if (categorySpend(data.transactions || [], owner, category, month) > budget.amount) over += 1;
  }
  return {
    due: {
      label: "Due",
      value: due === 0 ? "None due" : plural(due, "late", "late"),
      alert: due > 0,
      view: "bills" as const,
    },
    ious: {
      label: "IOUs",
      value: openIous === 0 ? "None open" : plural(openIous, "open", "open"),
      alert: false,
      view: "ious" as const,
    },
    budgets: {
      label: "Budgets",
      value: tracked === 0 ? "None set" : over === 0 ? "On track" : plural(over, "over", "over"),
      alert: over > 0,
      view: "budgets" as const,
    },
  };
}

export default function MoreMenu({ nav, data, today, month, profile, online = true }: MoreMenuProps) {
  const stats = hubStats(data, today, month, profile);
  const offlineTitle = online ? undefined : "Available when online";
  return (
    <section className="panel settings-panel settings-home">
      <div className="more-hub-stats" role="group" aria-label="Household snapshot">
        {([stats.due, stats.ious, stats.budgets] as const).map((stat) => (
          <button
            key={stat.view}
            type="button"
            className={stat.alert ? "is-alert" : undefined}
            onClick={() => nav(stat.view)}
          >
            <span>{stat.label}</span>
            <strong>{stat.value}</strong>
          </button>
        ))}
      </div>
      <h3 className="settings-group">Your money</h3>
      <button className="settings-link" onClick={() => nav("insights")}>
        <span className="settings-link-icon"><ChartNoAxesCombined size={20} /></span>
        <span className="settings-link-text"><strong>Insights</strong><small>Explore spending trends, income and what changed this month.</small></span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" onClick={() => nav("budgets")}>
        <span className="settings-link-icon">
          <PiggyBank size={20} />
        </span>
        <span className="settings-link-text">
          <strong>Budgets</strong>
          <small>
            Set spending caps per category and track progress.
          </small>
        </span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" onClick={() => nav("goals")}>
        <span className="settings-link-icon">
          <Target size={20} />
        </span>
        <span className="settings-link-text">
          <strong>Savings Goals</strong>
          <small>
            Target amounts, auto-accruing funds and wishlist milestones.
          </small>
        </span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" onClick={() => nav("trips")}>
        <span className="settings-link-icon">
          <Plane size={20} />
        </span>
        <span className="settings-link-text">
          <strong>Trips</strong>
          <small>
            Travel budgets, multi-currency cash wallets and trip expenses.
          </small>
        </span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" onClick={() => nav("ious")}>
        <span className="settings-link-icon"><HandCoins size={20} /></span>
        <span className="settings-link-text"><strong>IOUs</strong><small>Track money you've lent or borrowed until it's settled.</small></span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" onClick={() => nav("bills")}>
        <span className="settings-link-icon"><CalendarClock size={20} /></span>
        <span className="settings-link-text"><strong>Recurring</strong><small>Bills and regular income, due dates and history.</small></span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" disabled={!online} title={offlineTitle} onClick={() => nav("prices")}>
        <span className="settings-link-icon">
          <ShoppingBasket size={20} />
        </span>
        <span className="settings-link-text">
          <strong>Prices</strong>
          <small>{online ? "Compare brands, payees and prices over time." : "Needs a connection."}</small>
        </span>
        <ChevronRight size={18} />
      </button>
      <h3 className="settings-group">Set up</h3>
      <button className="settings-link" onClick={() => nav("account-settings")}>
        <span className="settings-link-icon"><Wallet size={20} /></span>
        <span className="settings-link-text"><strong>Accounts</strong><small>Create, edit, discontinue or remove accounts.</small></span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" onClick={() => nav("categories")}>
        <span className="settings-link-icon">
          <ReceiptText size={20} />
        </span>
        <span className="settings-link-text">
          <strong>Categories</strong>
          <small>
            Manage categories and subcategories used for new
            entries.
          </small>
        </span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" onClick={() => nav("payees")}>
        <span className="settings-link-icon"><Users size={20} /></span>
        <span className="settings-link-text"><strong>Payees</strong><small>Add and manage the people and businesses you deal with.</small></span>
        <ChevronRight size={18} />
      </button>
      <h3 className="settings-group">Looking back</h3>
      <button className="settings-link" disabled={!online} title={offlineTitle} onClick={() => nav("receipts")}>
        <span className="settings-link-icon"><Images size={20} /></span>
        <span className="settings-link-text">
          <strong>Receipts</strong>
          <small>{online ? "Browse saved receipt photos and the transactions they belong to." : "Needs a connection."}</small>
        </span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" onClick={() => nav("achievements")}>
        <span className="settings-link-icon"><Trophy size={20} /></span>
        <span className="settings-link-text">
          <strong>Achievements</strong>
          <small>See earned stars and this month’s progress.</small>
        </span>
        <ChevronRight size={18} />
      </button>
    </section>
  );
}
